/**
 * How many conversations each person in the inbox holds.
 *
 * The inbox is keyset-paged by `last_message_at`, so a person's applications
 * scatter across pages: counting within one page makes the row claim two where
 * there are four, then change its mind as the employer scrolls. So membership is
 * asked of the server, scoped to the loaded pages' people (at most `PAGE_SIZE`)
 * and hydrating only what is missing. Carries the panel's filters, so a folded
 * row never contains a chat the list is filtering out.
 */
import { useEffect, useMemo, useState } from 'react';
import type { StreamChat } from 'stream-chat';

import type { ChannelId, InboxItem, ParticipantId } from '../fixtures/domain.js';
import { needsReply } from '../fixtures/rules.js';
import {
  compileInboxQuery,
  filterOf,
  withFilter,
  withoutFilter,
  type InboxQuery,
} from '../inbox/query.js';
import { groupByPerson, type ApplicationSets } from '../inbox/stack.js';
import { cidParts } from '../lib/bridge.js';
import { queryInbox } from '../lib/facade.js';
import { absorbStageHints } from './inbox.js';
import { absorbUnlocks, hydrateChannels } from './stream.js';

/**
 * One key for a channel, whichever of its two names it arrives under.
 *
 * The read model names channels by bare id — that is what `hydrateChannels`
 * hands Stream's `id` filter — while a mapped `Channel.id` is the cid
 * (`messaging:abc`, see `mapChannel`). Correlating the two sides without this
 * fails silently and completely: every lookup misses, the map comes back empty,
 * and the inbox simply never folds a row.
 */
function keyOf(id: string): string {
  return cidParts(id).channelId;
}

/**
 * The ceiling on one membership answer.
 *
 * Bounds the hydration a single reply-storm can trigger. It is not a page size —
 * nothing pages this, and reaching it means the people on screen hold more
 * applications between them than the cap. The rows that fall off simply do not
 * fold, which is the pre-existing behaviour rather than a broken one.
 */
export const STACK_LIMIT = 200;

/** Channels an application set may reference that the loaded pages do not hold. */
type Extras = ReadonlyMap<ChannelId, InboxItem>;

interface Membership {
  /** The request this answers, so an answer can never outlive its own question. */
  key: string;
  channelIds: ChannelId[];
  extras: Extras;
}

/**
 * `status` REPLACES the caller's own: a row holds live conversations whatever
 * list it is drawn in, which matters for the widget's open-plus-ending list.
 *
 * `scope` is DROPPED. This asks what a person holds, not which of their chats
 * the Unread pill shows. Left on, opening one unread chat shrank the membership
 * and deleted the row `rowFate` had deliberately held (the #348 failure by
 * another route). The pill's cut is applied afterwards, in `applicationSetsOf`,
 * where the held row is still in hand.
 *
 * Exported for its own test: the stripped clauses are the whole behaviour.
 */
export function membershipRequest(query: InboxQuery, people: ParticipantId[]) {
  const scoped = withoutFilter(
    withFilter(withFilter(query, { kind: 'counterpart', counterpartIds: people }), {
      kind: 'status',
      phases: ['open'],
    }),
    'scope',
  );
  return { ...compileInboxQuery(scoped), limit: STACK_LIMIT };
}

/**
 * Every application held by each person the loaded pages show, keyed by person.
 *
 * Empty until the first answer lands, and empty again on failure — `stackRows`
 * folds nothing from an empty map, so the list degrades to the plain column it
 * has always been rather than to a half-folded one.
 *
 * `revision` re-asks when conversations arrive or leave (see `useInboxRevision`);
 * that is also what refreshes an application the loaded pages do not hold, since
 * those items are snapshots rather than live rows.
 */
export function useStackedApplications(
  query: InboxQuery,
  items: InboxItem[],
  client: StreamChat | null,
  revision = 0,
): ApplicationSets {
  const [answer, setAnswer] = useState<Membership | null>(null);

  // Everyone on screen, since a direct chat folds in with the applications now
  // (see `foldable`) and asking only about job-bound rows would leave a person
  // whose only loaded chat is a direct one out of their own row. Sorted, so the
  // same set of people in a different page order is the same question and does
  // not re-ask it.
  const people = useMemo(() => {
    const ids = new Set<ParticipantId>();
    for (const item of items) ids.add(item.counterpart.id);
    return [...ids].sort();
  }, [items]);

  const request = useMemo(
    () => (people.length === 0 ? null : membershipRequest(query, people)),
    [query, people],
  );

  const key = useMemo(() => (request ? JSON.stringify(request) : ''), [request]);
  const unreadOnly = filterOf(query, 'scope')?.value === 'unread';

  useEffect(() => {
    if (!client || !request) {
      setAnswer(null);
      return;
    }
    let alive = true;

    void (async () => {
      const { channel_ids, assistant_flagged, application_statuses, profile_unlocked_channels } =
        await queryInbox(request);
      if (!alive) return;
      absorbStageHints(application_statuses);
      absorbUnlocks(channel_ids, profile_unlocked_channels);

      // The loaded pages already hold nearly all of these. Hydrating them again
      // would re-watch channels this session is watching to produce items the
      // list is producing better — live ones.
      const loaded = new Set(items.map((item) => keyOf(item.channel.id)));
      // Passed on as they arrived, not as `keyOf` left them: these go back to
      // Stream's own `id` filter, which is the form the read model already uses.
      const missing = channel_ids.filter((id) => !loaded.has(keyOf(id)));
      // Rows reused from `items` already carry their verdict; only the ones
      // hydrated here have to be told, or a folded thread would be the one
      // place in the inbox a decision could hide.
      const hydrated =
        missing.length > 0
          ? await hydrateChannels(client, missing, new Set(assistant_flagged ?? []))
          : [];
      if (!alive) return;

      setAnswer({
        key,
        channelIds: channel_ids,
        extras: new Map(
          hydrated.map((m) => [
            keyOf(m.channel.id),
            { channel: m.channel, counterpart: m.counterpart, needsReply: needsReply(m.channel) },
          ]),
        ),
      });
    })().catch(() => {
      // Transient. Keep the last answer rather than unfolding every row on one
      // failed request — a list that re-splits into duplicate rows and back is
      // more disorienting than one that is briefly a reply behind.
    });

    return () => {
      alive = false;
    };
    // `items` is deliberately absent: it changes on every message, and the
    // membership question is about *people*, which `key` already names. The
    // loaded-item lookup inside is an optimisation over whatever is current
    // when the request resolves, not part of the question.
  }, [key, client, revision]);

  return useMemo(() => {
    if (!client) return new Map();
    // Held with the request it answers, so a filter change cannot leave rows folded
    // over a set the query no longer describes. Until the answer lands the page folds
    // what it holds itself: no extras, and a row stands where it will stand after.
    if (!answer || answer.key !== key) return groupByPerson(items);
    return applicationSetsOf(answer.channelIds, items, answer.extras, unreadOnly);
  }, [answer, key, items, unreadOnly, client]);
}

/**
 * The membership answer as items, keyed by person.
 *
 * Separated from the hook because it is the whole correctness of the feature and
 * the one part of it that is not about React: it correlates ids the read model
 * named with items Stream named, and those two use different forms (see
 * `keyOf`). Get it wrong and nothing throws — the map is merely empty, and the
 * inbox quietly goes on drawing one row per chat.
 */
export function applicationSetsOf(
  channelIds: readonly ChannelId[],
  items: InboxItem[],
  extras: Extras,
  unreadOnly = false,
): ApplicationSets {
  // Every key normalised on the way in, both sides, so no caller has to know
  // which of a channel's two names this map is keyed by. That knowledge is
  // exactly what the first cut of this got wrong, and getting it wrong is
  // invisible — see `keyOf`.
  const byId = new Map<string, InboxItem>();
  for (const item of items) byId.set(keyOf(item.channel.id), item);
  const loaded = new Set(byId.keys());
  // Loaded rows win: they are live, and the extras are snapshots taken when
  // membership was last asked.
  for (const [id, item] of extras) if (!byId.has(keyOf(id))) byId.set(keyOf(id), item);

  const sets = new Map<ParticipantId, InboxItem[]>();
  for (const id of channelIds) {
    const item = byId.get(keyOf(id));
    if (!item) continue;
    // Under the Unread pill a fold holds the pill's cut: unread chats, plus a
    // loaded row the list is still holding past a read (see `rowFate`).
    if (unreadOnly && !loaded.has(keyOf(id)) && item.channel.unreadCount <= 0) continue;
    const held = sets.get(item.counterpart.id);
    if (held) held.push(item);
    else sets.set(item.counterpart.id, [item]);
  }
  return sets;
}
